import { toast } from 'sonner';
import type { ApiResponse, OpenPeriodBlock } from '@repo/shared-types';
import { useAuthStore } from '../stores/authStore';
import { useUpgradeDialogStore } from '../stores/upgradeDialogStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Token refresh queue to prevent concurrent refresh calls
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}> = [];

/** Shape of a server guard's error body — extends the base `json.error` with
 * the fields tier guards attach (`feature` for FEATURE_LOCKED, `limitKind`
 * for TIER_LIMIT_REACHED) and the field `POST /schedule-templates/:id/apply`
 * attaches for `OPEN_PERIOD` (`block`) — so a caller whose preview went
 * stale can read the same block off the 409 without a second round trip.
 *
 * THI-267: `ClassGroupError`'s detail is spread flat into `error` too
 * (`apps/api/src/routes/classes.ts`, `{ code, message, ...detail }`) — these
 * fields cover `SLOT_TIME_CONFLICT` (`conflictingClassName`, `teacherName`,
 * `dayOfWeek`, `startTime`), `START_DATE_IN_RECONCILED_MONTH`
 * (`earliestAllowed`, an ISO date string over the wire), which
 * `ClassFormSheet` reads off the thrown error to tint the offending cell
 * instead of re-parsing the human sentence in `message`, and Task 13's own
 * `rowIndex`/`enrollmentId` — every row-specific `POST /classes/:id/members`
 * 400 (`DUPLICATE_ROW`, `PRICING_TYPE_MISMATCH`, `ENROLLMENT_PAUSED`,
 * `SLOT_NOT_IN_CLASS`, `RATE_REQUIRED`) carries `rowIndex`
 * (`classMemberService.ts`), which `AddMembersDialog`'s `ConfirmTable` reads
 * to jump back and mark the offending row instead of a bare toast.
 *
 * Fix wave: `START_DATE_BEFORE_CLASS` (any start-date path — batch add,
 * per-member slot edit, the class-context import commit) carries
 * `classStartsOn`, an ISO date string, for the same inline-under-the-date-
 * field treatment as `START_DATE_IN_RECONCILED_MONTH`. */
interface GuardErrorBody {
  code?: string;
  message?: string;
  feature?: string;
  limitKind?: string;
  block?: OpenPeriodBlock;
  conflictingClassName?: string;
  teacherName?: string;
  dayOfWeek?: number;
  startTime?: string;
  earliestAllowed?: string;
  classStartsOn?: string;
  rowIndex?: number;
  enrollmentId?: string;
}

/** Auto-opens the shared upgrade dialog when a response's error body signals
 * a tier guard (FEATURE_LOCKED / TIER_LIMIT_REACHED on students or seats). */
function maybeOpenUpgradeDialog(error: GuardErrorBody | undefined) {
  const code = error?.code;
  if (code === 'FEATURE_LOCKED') {
    useUpgradeDialogStore.getState().open(error?.feature);
  } else if (code === 'TIER_LIMIT_REACHED' && error?.limitKind === 'student') {
    useUpgradeDialogStore.getState().open('students');
  } else if (code === 'TIER_LIMIT_REACHED' && error?.limitKind === 'seat') {
    useUpgradeDialogStore.getState().open('seat');
  }
}

/** RBAC backstop: FE hides/disables admin-only actions (see useCan), but if a
 * gated request slips through anyway (stale UI, direct API call, race), the
 * API's `requirePermission` middleware rejects it with FORBIDDEN — surface
 * that as a plain toast rather than the upgrade dialog. */
function maybeToastForbidden(error: GuardErrorBody | undefined) {
  if (error?.code === 'FORBIDDEN') {
    toast.error(error.message || 'Forbidden');
  }
}

function processRefreshQueue(error: Error | null, token: string | null) {
  for (const { resolve, reject } of refreshQueue) {
    if (error) reject(error);
    else resolve(token!);
  }
  refreshQueue = [];
}

async function refreshAccessToken(): Promise<string> {
  const { refreshToken, updateTokens, clearAuth } = useAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    throw new Error('No refresh token');
  }

  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Token refresh failed');
  }

  const json = await res.json();
  if (!json.success) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Token refresh failed');
  }

  const tokens = json.data as { accessToken: string; refreshToken: string };
  updateTokens(tokens);
  return tokens.accessToken;
}

async function getValidToken(): Promise<string> {
  const { accessToken } = useAuthStore.getState();
  return accessToken || '';
}

export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getValidToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE}${endpoint}`;
  let response = await fetch(url, { ...options, headers });

  // Handle 401 — attempt token refresh
  if (response.status === 401 && token) {
    if (isRefreshing) {
      // Wait for the in-flight refresh to complete
      const newToken = await new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      });
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    } else {
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        processRefreshQueue(null, newToken);
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } catch (err) {
        processRefreshQueue(err as Error, null);
        throw err;
      } finally {
        isRefreshing = false;
      }
    }
  }

  const text = await response.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse JSON response: ${text}`);
  }

  if (!json.success) {
    const errBody = json.error as GuardErrorBody | undefined;
    maybeOpenUpgradeDialog(errBody);
    maybeToastForbidden(errBody);
    const error = new Error(errBody?.message || 'API Error') as Error & {
      code?: string;
      status?: number;
      block?: OpenPeriodBlock;
      conflictingClassName?: string;
      teacherName?: string;
      dayOfWeek?: number;
      startTime?: string;
      earliestAllowed?: string;
      classStartsOn?: string;
      rowIndex?: number;
      enrollmentId?: string;
    };
    error.code = errBody?.code;
    error.status = response.status;
    error.block = errBody?.block;
    error.conflictingClassName = errBody?.conflictingClassName;
    error.teacherName = errBody?.teacherName;
    error.dayOfWeek = errBody?.dayOfWeek;
    error.startTime = errBody?.startTime;
    error.earliestAllowed = errBody?.earliestAllowed;
    error.classStartsOn = errBody?.classStartsOn;
    error.rowIndex = errBody?.rowIndex;
    error.enrollmentId = errBody?.enrollmentId;
    throw error;
  }

  return (json as { success: true; data: T }).data;
}

/** Fetch without auth — for public endpoints (login, register, etc.) */
export async function fetchPublicApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const text = await response.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse JSON response: ${text}`);
  }

  if (!json.success) {
    const error = new Error(json.error?.message || 'API Error') as Error & {
      code?: string;
      status?: number;
    };
    error.code = json.error?.code;
    error.status = response.status;
    throw error;
  }

  return (json as { success: true; data: T }).data;
}

/**
 * Like fetchApi but returns the raw Response — for binary downloads (xlsx,
 * PDF, etc.) where we don't want JSON auto-parsing. Same auth + refresh
 * behavior as fetchApi.
 */
export async function fetchApiBlob(
  endpoint: string,
  options?: RequestInit,
): Promise<Response> {
  const token = await getValidToken();

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE}${endpoint}`;
  let response = await fetch(url, { ...options, headers });

  if (response.status === 401 && token) {
    if (isRefreshing) {
      const newToken = await new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      });
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    } else {
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        processRefreshQueue(null, newToken);
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } catch (err) {
        processRefreshQueue(err as Error, null);
        throw err;
      } finally {
        isRefreshing = false;
      }
    }
  }

  if (!response.ok) {
    const text = await response.text();
    let json: { success: false; error?: GuardErrorBody } | undefined;
    try {
      json = JSON.parse(text);
    } catch {
      // Non-JSON error body — fall through to a generic error below.
    }
    const errBody = json?.error;
    maybeOpenUpgradeDialog(errBody);
    maybeToastForbidden(errBody);
    const error = new Error(errBody?.message || 'API Error') as Error & {
      code?: string;
      status?: number;
    };
    error.code = errBody?.code;
    error.status = response.status;
    throw error;
  }

  return response;
}
